import { StrictMode, useEffect } from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';

import clarity from '@microsoft/clarity';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';

import { App } from '@/app/app';

import { log } from '@/shared/lib/logging';

import pack from '../package.json';

import { enableMocking } from './mocks/init';

const getEnvironment = () => {
    if (__IS_DEV__) {
        return 'development';
    }

    if (__IS_STAGE__) {
        return 'staging';
    }

    return 'production';
};

const getTracesSampleRate = () => {
    if (__IS_DEV__ || __IS_STAGE__) {
        return 1.0;
    }

    return 0.1;
};

Sentry.init({
    dsn: 'https://e24d9a89d822773128faed0da2719f95@o4510153498624000.ingest.us.sentry.io/4510153499934720',
    environment: getEnvironment(),
    sendDefaultPii: true,
    release: `algotech-frontend@${pack.version}`,
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration(),
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: getTracesSampleRate(),
    tracePropagationTargets: [
        'localhost',
        /^https:\/\/.*\.algotechnologies\.ai/,
    ],
    enabled: true,
});

if (!__IS_DEV__ && !__IS_STAGE__) {
    clarity.init('tmzbaxdmov');
}

log('App version', pack.version);

const container = document.getElementById('root');

if (!container) {
    throw new Error("The root container not found. Can't mount React app");
}

const initializeApp = async () => {
    // MSW mocking: only load in dev/stage, API_MODE check is done inside enableMocking()
    // to avoid tree-shaking when the placeholder isn't replaced yet at build time
    if (__IS_DEV__ || __IS_STAGE__) {
        try {
            await enableMocking();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('[MSW] Failed to initialize mocking:', error);
        }
    }

    const root = createRoot(container);

    root.render(
        <StrictMode>
            <App />
        </StrictMode>,
    );
};

initializeApp().catch((error: unknown) => {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize app:', error);
});
